import { motion, AnimatePresence } from 'motion/react';
import { 
  Wind, Moon, Sun, MapPin, ArrowRight, ShieldCheck, 
  Menu, X, Phone, Check, MessageCircle, Instagram, 
  ChevronDown, AlertTriangle, Bug, Zap, Droplets, CreditCard, Sparkles, Navigation, Euro
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CookieConsent from './components/CookieConsent';


export default function App() {
  const [isDark, setIsDark] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [isPricingExpanded, setIsPricingExpanded] = useState(false);
  const showGallery = false; // Nastavte na true pre zobrazenie galérie.

  // Dynamic CMS state
  const [texts, setTexts] = useState<any>({
    heroTitle: "Hĺbkové čistenie klimatizácií Žilina",
    heroSubtitle: "Dýchajte opäť zdravý vzduch. Zabezpečíme profesionálne hĺbkové čistenie vašej klimatizácie v Žiline a okolí. Rýchlo, čisto a bez starostí.",
    stats1: "500+",
    stats1Label: "Vyčistených klím v Žiline",
    stats2: "100%",
    stats2Label: "Spokojných klientov",
    stats3: "24h",
    stats3Label: "Rýchlosť servisu",
    pricingTitle: "Jasný Cenník, Žiadne Prekvapenia",
    pricingSubtitle: "Ceny čistenia klímy sú konečné, vrátane dopravy v rámci Žiliny a okolia.",
    contactTitle: "ZABEZPEČTE SI TERMÍN NA ČISTENIE KLÍMY.",
    contactSubtitle: "Kým neudrú letné horúčavy, máme termíny na čistenie klimatizácií v Žiline voľné s veľmi rýchlym vybavením. Napíšte nám.",
    contactFormTitle: "Rezervovať hĺbkové čistenie",
    footerText: "Hĺbkové čistenie klimatizácií Žilina, Slovensko",
    whyUsTitle: "Prečo nás zavolať?",
    contactPhone: "+421 911 531 673"
  });
  const [pricing, setPricing] = useState<any>({
    nastenna: { regular: 100, retail: 80, b2b_mid: 60, b2b_large: 50 },
    multisplit: { regular: 70, retail: 49, b2b_mid: 40, b2b_large: 30 },
    kazetova_stropna: { regular: 130, retail: 115, b2b_mid: 95, b2b_large: 85 },
    parapetna: { regular: 110, retail: 95, b2b_mid: 75, b2b_large: 65 },
    vonkajsia: { regular: 60, retail: 50, b2b_mid: 50, b2b_large: 50 },
    mobilna: { regular: 55, retail: 45, b2b_mid: 40, b2b_large: 35 },
    discounts: { tier1: 5, tier1_min: 3, tier2: 10, tier2_min: 5, tier3: 15, tier3_min: 10 },
    additionalServices: [
        { id: '1', name: 'Diagnostika okruhu', price: 20 },
        { id: '2', name: 'Doplnenie chladiva R32 / R410A (za každých 100g)', price: 10 }
    ]
  });
  const [seoLocalities, setSeoLocalities] = useState("Martin, Čadca, Bytča, Kysucké Nové Mesto, Považská Bystrica");
  const [galleryImages, setGalleryImages] = useState<any[]>([]);

  const isHighSeason = [4, 5, 6, 7].includes(new Date().getMonth());

  const getPriceData = (category: string, defaultRegular: number, defaultRetail: number) => {
    const regular = Number(pricing?.[category]?.regular || defaultRegular);
    const retail = Number(pricing?.[category]?.retail || defaultRetail);
    
    if (isHighSeason) {
      return { current: regular, crossed: null };
    }
    return { current: retail, crossed: regular > retail ? regular : null };
  };

  // Contact Form State
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    type: '1',
    location: '',
    captcha: '',
    honeypot: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formErrorMessage, setFormErrorMessage] = useState('');

  useEffect(() => {
    // Generate simple math captcha
    setNum1(Math.floor(Math.random() * 10) + 1);
    setNum2(Math.floor(Math.random() * 10) + 1);

    // Track visit
    if (!sessionStorage.getItem('visit_tracked')) {
        fetch(import.meta.env.BASE_URL + 'api/stats.php', { method: 'POST' }).catch(() => {});
        sessionStorage.setItem('visit_tracked', '1');
    }
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormErrorMessage('');

    try {
      const res = await fetch(import.meta.env.BASE_URL + 'contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          location: formData.location,
          type: formData.type,
          honeypot: formData.honeypot,
          captchaExpected: String(num1 + num2),
          captcha: formData.captcha
        })
      });
      const data = await res.json();

      if (data.success) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', type: '1', location: '', captcha: '', honeypot: '' });
        // Generate new captcha numbers to be safe
        setNum1(Math.floor(Math.random() * 10) + 1);
        setNum2(Math.floor(Math.random() * 10) + 1);
      } else {
        setFormStatus('error');
        setFormErrorMessage(data.error || 'Nastala chyba pri odosielaní.');
      }
    } catch {
      setFormStatus('error');
      setFormErrorMessage('Nepodarilo sa spojiť so serverom. Skúste to znova.');
    }
  };

  useEffect(() => {
    // Load config from PHP
    fetch(import.meta.env.BASE_URL + 'api/config.php')
      .then(res => {
         if (!res.ok) throw new Error("Network not ok");
         return res.text();
      })
      .then(text => {
         try {
           return JSON.parse(text);
         } catch(e) {
           throw new Error("Invalid JSON from server");
         }
      })
      .then(data => {
        if (data && data.texts && Object.keys(data.texts).length > 0) {
          setTexts((prev: any) => {
            const newTexts = { ...prev };
            for (const key in data.texts) {
              if (data.texts[key]) {
                if (typeof data.texts[key] === 'string' && data.texts[key].trim() === '') {
                  continue; // Skip empty strings
                }
                newTexts[key] = data.texts[key];
              }
            }
            return newTexts;
          });
        }
        if (data && data.pricing && Object.keys(data.pricing).length > 0) {
          setPricing((prev: any) => ({ ...prev, ...data.pricing }));
        }
        if (data && data.seoLocalities) setSeoLocalities(data.seoLocalities);
      })
      .catch(console.error);

    const savedSeo = localStorage.getItem('klimacisto_seo');
    if (savedSeo) {
      setSeoLocalities(savedSeo);
    }

    // Load gallery
    fetch(import.meta.env.BASE_URL + 'api/public/gallery.php')
      .then(res => res.json())
      .then(data => {
        if (data.gallery && data.gallery.length > 0) {
          setGalleryImages(data.gallery);
        }
      })
      .catch(console.error);

    // Load FAQs
    fetch(import.meta.env.BASE_URL + 'api/public/faq.php')
      .then(res => res.json())
      .then(data => {
        if (data.faqs && data.faqs.length > 0) {
          setFaqs(data.faqs);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.style.colorScheme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  const navLinks = [
    { name: 'Prečo my', id: 'problem' },
    { name: 'Cenník', id: 'cennik' },
    { name: 'Lokality', id: 'lokality' },
    { name: 'Časté otázky', id: 'faq' },
  ];

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const [faqs, setFaqs] = useState([
    {
      q: "Ako často treba čistiť klimatizáciu?",
      a: "Pre zabezpečenie zdravého vzduchu a maximálnej účinnosti odporúčame hĺbkové čistenie minimálne raz ročne – ideálne pred letnou sezónou alebo po nej. Ak klimatizáciu v zime aktívne využívate aj na kúrenie, najlepšie pre vaše zdravie sú 2 servisné intervaly ročne."
    },
    {
      q: "Naozaj neurobíte pri čistení neporiadok na stene?",
      a: "Garantujeme čistú prácu. Štandardne pracujeme s ochrannými návlekmi na topánky a pred čistením nasadíme na klimatizáciu špeciálny zberný vak. Odmontujeme plasty a vnútro pod tlakom vyperieme tak, že všetka voda a nečistoty stečú priamo do našej nádoby. Vaša stena aj podlaha zostanú 100% čisté."
    },
    {
      q: "Oplatí sa priplácať za extra ozónové čistenie?",
      a: "Ak niekto ponúka iba ozón bez toho, aby fyzicky rozobral klímu a vyčistil ju od pevných nánosov prachu a nánosov plesní v rotore, je to len marketingový trik. Samotný ozón prach z vnútra neodstráni. Naším štandardom je dôkladné mechanické odstránenie nánosov chémiou a tlakom vody. Následne použijeme parnú dezinfekciu, ktorá bezpečne zničí mikroorganizmy."
    },
    {
      q: "Je pravda, že čistá klimatizácia mi ušetrí peniaze?",
      a: "Áno. Zanesený rotor je ťažký a neprepúšťa dostatok vzduchu, takže kompresor musí pracovať dlhšie a na vyšší výkon. Po hĺbkovom vyčistení stúpne prietok vzduchu, zníži sa hlučnosť a celková úspora na elektrickej energii sa môže po sezóne vyšplhať aj na zreteľných 20-30%."
    }
  ]);

  const phoneWithoutSpaces = texts.contactPhone?.replace(/\s+/g, '') || "+421911531673";

  return (
    <div className="min-h-screen selection:bg-cyan-500/30 flex flex-col">
      {/* NAVIGATION */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#09090b]/80 border-b border-zinc-200 dark:border-zinc-800/50 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
           <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo(0,0)}>
             <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400 flex items-center justify-center transition-colors">
               <Wind className="w-6 h-6" />
             </div>
            <span className="font-extrabold tracking-tight text-2xl dark:text-zinc-50 hover:text-cyan-500 transition-colors">KlímaČisto</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <button key={link.name} onClick={() => scrollTo(link.id)} className="text-sm font-bold text-zinc-600 dark:text-zinc-300 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors uppercase tracking-wider">
                {link.name}
              </button>
            ))}
            <div className="flex items-center gap-4 border-l border-zinc-200 dark:border-zinc-800 pl-8 transition-colors">
              <button onClick={toggleTheme} className="p-2.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-zinc-600 dark:text-zinc-400" aria-label="Prepnúť režim">
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button onClick={() => scrollTo('kontakt')} className="bg-cyan-500 text-zinc-950 px-7 py-3 rounded-full text-sm font-black hover:bg-cyan-400 transition-colors uppercase tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.3)]">
                Rezervovať
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center gap-3">
             <button onClick={toggleTheme} className="p-2 rounded-full text-zinc-600 dark:text-zinc-400" aria-label="Prepnúť režim">
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-zinc-600 dark:text-zinc-400" aria-label="Menu">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-zinc-50 dark:bg-[#09090b] pt-24 px-6 md:hidden transition-colors duration-500 overflow-y-auto"
          >
            <div className="flex flex-col gap-6 text-2xl font-bold pb-10">
               {navLinks.map(link => (
                <button key={link.name} onClick={() => scrollTo(link.id)} className="text-left text-zinc-600 dark:text-zinc-400 hover:text-cyan-500 transition-colors uppercase tracking-tight">
                  {link.name}
                </button>
              ))}
              <div className="mt-8 flex flex-col gap-4">
                <button onClick={() => scrollTo('kontakt')} className="bg-cyan-500 text-zinc-950 px-6 py-4 rounded-full text-center active:scale-95 transition-transform font-black uppercase tracking-wider text-lg">
                    Rezervovať termín
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <main className="flex-grow">

      {/* HERO SECTION */}
      <section className="pt-32 pb-16 md:pt-56 md:pb-32 px-4 sm:px-6 max-w-7xl mx-auto relative overflow-hidden md:overflow-visible">
        <h1 className="sr-only">Hĺbkové čistenie klimatizácií Žilina</h1>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] sm:w-[800px] sm:h-[800px] bg-cyan-500/15 dark:bg-cyan-500/10 blur-[130px] rounded-full -z-10 pointer-events-none transition-colors duration-500"></div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col text-center items-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 px-5 py-3 sm:py-2 rounded-[2rem] sm:rounded-full border border-zinc-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/50 backdrop-blur-sm text-[10px] sm:text-xs font-black uppercase tracking-widest mb-10 text-cyan-700 dark:text-cyan-400 transition-colors shadow-sm text-center">
             <ShieldCheck className="w-4 h-4 shrink-0" />
             <span>Najlepšie hĺbkové čistenie klimatizácií v Žiline a okolí</span>
          </div>
          
          <h2 className="text-6xl sm:text-7xl lg:text-[100px] xl:text-[120px] font-black tracking-tighter leading-none mb-8 text-zinc-900 dark:text-zinc-50 transition-colors duration-500">
            NÁDYCH.<br />
            VÝDYCH.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-700 to-blue-600 dark:from-cyan-400 dark:to-blue-500 inline-block pt-3">ČISTO.</span>
          </h2>

          <p className="text-lg md:text-2xl max-w-3xl text-zinc-600 dark:text-zinc-400 mb-12 leading-relaxed font-semibold transition-colors tracking-tight whitespace-pre-line px-4 md:px-0">
            {texts.heroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 w-full sm:w-auto mt-6">
            <a href={`tel:${phoneWithoutSpaces}`} className="min-h-[56px] px-8 py-5 bg-cyan-500 text-zinc-950 rounded-full font-black text-lg hover:bg-cyan-400 hover:scale-105 transition-all flex items-center justify-center gap-3 uppercase tracking-wider shadow-[0_0_30px_rgba(6,182,212,0.3)]">
              <Phone className="w-6 h-6 shrink-0" /> Zavolať teraz
            </a>
            <a href={`https://wa.me/${phoneWithoutSpaces}`} target="_blank" rel="noreferrer" className="min-h-[56px] px-8 py-5 bg-[#25D366]/10 text-[#128c3e] dark:text-[#25D366] hover:bg-[#25D366]/20 border border-[#25D366]/30 dark:border-[#25D366]/20 rounded-full font-black text-lg transition-colors flex items-center justify-center gap-3 uppercase tracking-wider text-center">
              <MessageCircle className="w-6 h-6 shrink-0" /> WhatsApp rezervácia
            </a>
          </div>
        </motion.div>
      </section>

      {/* PREČO HĹBKOVÉ ČISTENIE (E-E-A-T) */}
      <section id="problem" className="py-16 md:py-24 px-4 sm:px-6 max-w-7xl mx-auto border-t border-zinc-200 dark:border-zinc-800/50">
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4 md:mb-6 text-zinc-900 dark:text-zinc-50 transition-colors">ZA LÁKAVÝM CHLADOM<br/><span className="text-zinc-600 dark:text-zinc-400 font-medium tracking-normal text-3xl md:text-5xl align-middle">sa skrýva hrozba.</span></h2>
          <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-400 max-w-3xl font-semibold leading-relaxed">Poutieranie prachu a nasprejovanie filtra nestačí. Vnútro vašej klimatizácie je vlhkým inkubátorom, z ktorého dýchate toxíny.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* PROBLEM SIDE */}
          <div className="bg-zinc-100 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800/50 rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-12 relative overflow-hidden transition-colors">
            <div className="inline-flex items-center gap-2 text-zinc-600 dark:text-zinc-400 font-black uppercase tracking-widest text-xs md:text-sm mb-6 md:mb-8 bg-zinc-200 dark:bg-zinc-800 px-4 py-2 rounded-full">
              Realita bez servisu
            </div>
             <h3 className="text-2xl md:text-3xl font-black mb-6 md:mb-8 text-zinc-900 dark:text-zinc-50">Čo doma naozaj dýchate?</h3>
            <ul className="space-y-6 md:space-y-8 relative z-10">
              <li className="flex gap-4 md:gap-5">
                <div className="mt-1 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white dark:bg-zinc-800 rounded-xl md:rounded-2xl text-zinc-500 dark:text-zinc-400 shrink-0 shadow-sm"><Bug className="w-5 h-5 md:w-6 md:h-6" /></div>
                <div>
                  <h4 className="font-extrabold text-zinc-900 dark:text-zinc-100 text-lg md:text-xl mb-1">Nánosy prachu a alergény</h4>
                  <p className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base font-medium">Tmavé a vlhké prostredie nenačistenej klímy je ideálnym miestom pre šírenie prachu, roztočov a plesní, ktoré môžu dráždiť dýchacie cesty a spôsobovať alergie.</p>
                </div>
              </li>
              <li className="flex gap-4 md:gap-5">
                <div className="mt-1 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white dark:bg-zinc-800 rounded-xl md:rounded-2xl text-zinc-500 dark:text-zinc-400 shrink-0 shadow-sm"><Zap className="w-5 h-5 md:w-6 md:h-6" /></div>
                <div>
                  <h4 className="font-extrabold text-zinc-900 dark:text-zinc-100 text-lg md:text-xl mb-1">Vyššia spotreba a hlučnosť</h4>
                  <p className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base font-medium">Zanesený ventilátor (rotor) musí na pretlačenie vzduchu vynaložiť oveľa viac energie. Klimatizácia je hlučnejšia a účet za elektrinu zbytočne stúpa.</p>
                </div>
              </li>
            </ul>
          </div>

          {/* SOLUTION SIDE */}
          <div className="bg-cyan-50 dark:bg-[#121214] border-2 border-cyan-500/50 dark:border-cyan-500/50 rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-12 relative overflow-hidden transition-colors shadow-[0_0_40px_rgba(6,182,212,0.1)]">
            <div className="absolute -bottom-10 -right-10 opacity-[0.03] dark:opacity-[0.05] text-cyan-500 pointer-events-none"><Sparkles className="w-64 h-64" /></div>
            <div className="inline-flex items-center gap-2 text-cyan-700 dark:text-cyan-400 font-black uppercase tracking-widest text-xs md:text-sm mb-6 md:mb-8 bg-cyan-100 dark:bg-cyan-900/50 px-4 py-2 rounded-full shadow-sm">
              Naše riešenie: KlímaČisto
            </div>
            <h3 className="text-2xl md:text-3xl font-black mb-6 md:mb-8 text-zinc-900 dark:text-zinc-50">Hĺbkové čistenie klimatizácií v Žiline</h3>
            <ul className="space-y-6 md:space-y-8 relative z-10">
               <li className="flex gap-4 md:gap-5">
                <div className="mt-1 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-cyan-100 dark:bg-cyan-900/50 rounded-xl md:rounded-2xl text-cyan-600 dark:text-cyan-400 shrink-0"><ShieldCheck className="w-5 h-5 md:w-6 md:h-6" /></div>
                 <div>
                  <h4 className="font-extrabold text-zinc-900 dark:text-zinc-100 text-lg md:text-xl mb-1">Poctivé hĺbkové čistenie s vakom</h4>
                  <p className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base font-medium">Demontujeme plastový kryt, nasadíme ochranný vak a pod bezpečným tlakom vody kompletne vyperieme roky usadených nečistôt priamo do zbernej nádoby. Vaša stena, podlaha aj nábytok zostanú stopercentne čisté.</p>
                </div>
              </li>
              <li className="flex gap-4 md:gap-5">
                <div className="mt-1 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-cyan-100 dark:bg-cyan-900/50 rounded-xl md:rounded-2xl text-cyan-600 dark:text-cyan-400 shrink-0"><Droplets className="w-5 h-5 md:w-6 md:h-6" /></div>
                <div>
                  <h4 className="font-extrabold text-zinc-900 dark:text-zinc-100 text-lg md:text-xl mb-1">Parná dezinfekcia a maximálny výkon</h4>
                  <p className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base font-medium">Po vyčistení aplikujeme parnú dezinfekciu a skontrolujeme funkčnosť. Domov odchádzame až vtedy, keď vaša klíma funguje ticho, úsporne a fúka dokonale svieži vzduch. Celý proces trvá asi 45-60 minút.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CENNÍK */}
      <section id="cennik" className="py-16 md:py-24 px-4 sm:px-6 max-w-7xl mx-auto border-t border-zinc-200 dark:border-zinc-800/50">
        <div className="text-center mb-12 md:mb-16">
           <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4 text-zinc-900 dark:text-zinc-50 transition-colors whitespace-pre-line">{texts.pricingTitle}</h2>
           <p className="text-lg text-zinc-600 dark:text-zinc-400 font-semibold whitespace-pre-line px-2">{texts.pricingSubtitle}</p>
        </div>
        
        <div className="grid lg:grid-cols-12 gap-8 items-start">
           
           {/* MAIN PRICING TIER */}
           <div className="lg:col-span-7 rounded-[2rem] md:rounded-[3rem] border border-zinc-200 dark:border-zinc-800 bg-white/50 dark:bg-[#121214]/50 backdrop-blur-xl p-6 md:p-12 relative shadow-2xl transition-all duration-500">
             <div className="absolute top-0 right-6 md:right-10 -translate-y-1/2 bg-cyan-500 text-zinc-950 text-[10px] md:text-xs font-black uppercase tracking-widest px-4 md:px-6 py-2 rounded-full shadow-lg shadow-cyan-500/30">Hĺbka Premium</div>
             
             <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 dark:border-zinc-800 pb-6 md:pb-8 mb-6 md:mb-8 gap-4">
               <div>
                  <div className="text-cyan-600 dark:text-cyan-400 font-black tracking-widest uppercase text-xs md:text-sm mb-2 flex items-center gap-2"><Wind className="w-4 h-4 md:w-5 md:h-5"/> Kompletné čistenie</div>
                  <div className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base font-bold mb-1">Trvanie: 45 - 60 minút</div>
                  <h3 className="text-2xl md:text-3xl font-extrabold text-zinc-900 dark:text-zinc-50">1x Vnútorná + 1x Vonkajšia</h3>
               </div>
               <div className="flex items-center gap-3 md:gap-4">
                 {getPriceData('nastenna', 100, 79).crossed && <div className="text-2xl md:text-4xl font-bold text-zinc-500 dark:text-zinc-400 line-through decoration-red-500/50">{getPriceData('nastenna', 100, 79).crossed}€</div>}
                 <div className="text-5xl md:text-7xl font-black text-zinc-900 dark:text-zinc-50">{getPriceData('nastenna', 100, 79).current}€</div>
               </div>
             </div>

             <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 md:gap-y-5 mb-8 md:mb-10">
               {[
                 'Záchytný vak na ochranu steny', 
                 'Tlakové čistenie rotora a lamiel', 
                 'Profi chémia proti plesniam', 
                 'Prečistenie odtoku kondenzátu/čerpadla',
                 'Očistenie vonkajšej jednotky',
                 'Kontrola funkčnosti a tlakov'
                ].map(t => (
                 <li key={t} className="flex gap-3 md:gap-4 text-zinc-700 dark:text-zinc-300 font-bold items-start text-base md:text-lg">
                   <Check className="w-5 h-5 md:w-6 md:h-6 mt-0 md:mt-0.5 text-cyan-500 shrink-0" /> {t}
                 </li>
               ))}
             </ul>
             <button onClick={() => scrollTo('kontakt')} className="w-full py-5 md:py-6 rounded-2xl bg-zinc-900 dark:bg-zinc-50 text-white dark:text-zinc-950 font-black uppercase tracking-widest hover:scale-[1.02] active:scale-[0.98] transition-all text-base md:text-lg flex justify-center items-center gap-3">
               Rezervovať termín <ArrowRight className="w-5 h-5" />
             </button>
           </div>

           {/* SIDE BLOCKS (Multi-Split & Transport) */}
           <div className="lg:col-span-5 flex flex-col gap-6">
             {/* Multi Split */}
             <div className="rounded-[2rem] md:rounded-[2.5rem] bg-zinc-100 dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 p-6 md:p-10 flex flex-col transition-colors">
                 <div className="mb-4 text-zinc-600 dark:text-zinc-400 flex items-center gap-3">
                    {getPriceData('multisplit', 70, 49).crossed && <span className="font-bold text-lg text-zinc-500 dark:text-zinc-400 line-through decoration-red-500/50">{getPriceData('multisplit', 70, 49).crossed}€</span>}
                    <span className="font-extrabold text-2xl text-zinc-900 dark:text-zinc-100">{getPriceData('multisplit', 70, 49).current}€</span> / ks
                 </div>
                 <h4 className="font-black text-2xl text-zinc-900 dark:text-zinc-50 mb-3">Máte Multi-Split?</h4>
                 <p className="text-zinc-600 dark:text-zinc-400 font-semibold leading-relaxed">
                   Ak máte v rodinnom dome alebo firme viac vnútorných jednotiek zapojených na jednu vonkajšiu, <strong className="text-zinc-900 dark:text-zinc-200">každá ďalšia vnútorná jednotka vás stojí už len {getPriceData('multisplit', 70, 49).current} €.</strong> (+ 30-45 min/ks)
                 </p>
             </div>
             
             {/* Transport */}
             <div className="rounded-[2rem] md:rounded-[2.5rem] bg-[#09090b] dark:bg-[#1a1a1e] border border-zinc-800 p-6 md:p-10 flex flex-col transition-colors text-zinc-50 overflow-hidden relative">
                 <div className="absolute -bottom-8 -right-8 opacity-10 text-white"><Navigation className="w-48 h-48" /></div>
                 <h4 className="font-black text-2xl mb-6 relative z-10 flex items-center gap-3"><Euro className="w-7 h-7 text-cyan-500" /> Doprava k Vám</h4>
                 <ul className="space-y-4 relative z-10 font-bold">
                    <li className="flex justify-between items-center border-b border-zinc-800 pb-3">
                      <span className="text-zinc-400">{pricing?.transport?.freeZone || 'Žilina a okolie (do 15 km)'}</span>
                      <span className="text-cyan-400 bg-cyan-500/10 px-3 py-1 rounded-lg">ZADARMO</span>
                    </li>
                    <li className="flex justify-between items-center border-b border-zinc-800 pb-3">
                      <span className="text-zinc-400">{pricing?.transport?.paidZone || 'Okresy ZA Kraja'}</span>
                      <span className="text-zinc-200">{pricing?.transport?.paidZonePrice || '0,50 € / km'}</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="text-zinc-400">{pricing?.transport?.farZone || 'Mimo kraja (Celé SK)'}</span>
                      <span className="text-zinc-200">{pricing?.transport?.farZonePrice || 'Individuálne'}</span>
                    </li>
                 </ul>
             </div>
           </div>
           
           {/* COMPLETE PRICING TOGGLE */}
           <div className="lg:col-span-12 mt-4">
             <button 
               onClick={() => setIsPricingExpanded(!isPricingExpanded)}
               className="mx-auto flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400 font-bold uppercase tracking-widest text-sm hover:text-cyan-500 transition-colors bg-white/50 dark:bg-zinc-900/50 backdrop-blur-sm border border-zinc-200 dark:border-zinc-800 rounded-full px-8 py-4 w-full md:w-auto"
             >
               {isPricingExpanded ? 'Skryť kompletný cenník' : 'Zobraziť kompletný cenník'}
               <ChevronDown className={`w-5 h-5 transition-transform ${isPricingExpanded ? 'rotate-180' : ''}`} />
             </button>

             <AnimatePresence>
                {isPricingExpanded && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid md:grid-cols-2 gap-8 pt-8">
                       {/* TABLE with other types */}
                       <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 shadow-sm">
                         <h4 className="font-black text-xl mb-6 text-zinc-900 dark:text-zinc-50 border-b border-zinc-100 dark:border-zinc-800 pb-4">Iné typy klimatizácií</h4>
                         <ul className="space-y-5">
                           <li className="flex justify-between items-center">
                              <span className="font-bold text-zinc-700 dark:text-zinc-300">Stropná kazetová</span>
                              <div className="flex items-center gap-3">
                                 {getPriceData('kazetova_stropna', 130, 115).crossed && <span className="text-zinc-400 line-through text-sm font-bold">{getPriceData('kazetova_stropna', 130, 115).crossed}€</span>}
                                 <span className="font-black text-zinc-900 dark:text-zinc-50 text-xl">{getPriceData('kazetova_stropna', 130, 115).current}€</span>
                              </div>
                           </li>
                           <li className="flex justify-between items-center">
                              <span className="font-bold text-zinc-700 dark:text-zinc-300">Parapetná / podstropná</span>
                              <div className="flex items-center gap-3">
                                 {getPriceData('parapetna', 110, 95).crossed && <span className="text-zinc-400 line-through text-sm font-bold">{getPriceData('parapetna', 110, 95).crossed}€</span>}
                                 <span className="font-black text-zinc-900 dark:text-zinc-50 text-xl">{getPriceData('parapetna', 110, 95).current}€</span>
                              </div>
                           </li>
                           <li className="flex justify-between items-center pt-2">
                              <span className="font-bold text-zinc-700 dark:text-zinc-300">Mobilná klimatizácia</span>
                              <div className="flex items-center gap-3">
                                 {getPriceData('mobilna', 55, 45).crossed && <span className="text-zinc-400 line-through text-sm font-bold">{getPriceData('mobilna', 55, 45).crossed}€</span>}
                                 <span className="font-black text-zinc-900 dark:text-zinc-50 text-xl">{getPriceData('mobilna', 55, 45).current}€</span>
                              </div>
                           </li>
                           <li className="flex justify-between items-center">
                              <span className="font-bold text-zinc-700 dark:text-zinc-300">Kanálová klimatizácia</span>
                              <span className="font-black text-zinc-900 dark:text-zinc-50 border border-zinc-200 dark:border-zinc-700 px-3 py-1 rounded-lg text-sm bg-zinc-50 dark:bg-zinc-800">Na vyžiadanie</span>
                           </li>
                         </ul>
                       </div>

                       {/* INCLUDED & EXCLUDED */}
                       <div className="space-y-6">
                          <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 shadow-sm">
                            <h4 className="font-black text-xl mb-4 text-zinc-900 dark:text-zinc-50 border-b border-zinc-100 dark:border-zinc-800 pb-4 flex items-center gap-2"><Check className="text-cyan-500 w-5 h-5"/> Čo presne dostanete v cene servisu</h4>
                            <ul className="space-y-2.5 text-sm text-zinc-600 dark:text-zinc-400 font-medium list-disc pl-5">
                               {(texts?.includedServices || [
                                 "Demontáž plastových krytov a filtrov",
                                 "Nasadenie špeciálneho ochranného vaku okolo celej jednotky",
                                 "Tlakové pranie turbíny a výparníka špeciálnou chémiou",
                                 "Preplach odtoku kondenzátu pod tlakom",
                                 "Základné očistenie a kontrola vonkajšej jednotky",
                                 "Meranie tlakov chladiva a kontrola funkčnosti",
                                 "Potvrdenie záruky dodávateľovi"
                               ]).map((item: string, idx: number) => (
                                 <li key={idx}>{item}</li>
                               ))}
                            </ul>
                          </div>

                          <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 shadow-sm">
                            <h4 className="font-black text-xl mb-4 text-zinc-900 dark:text-zinc-50 border-b border-zinc-100 dark:border-zinc-800 pb-4 flex items-center gap-2"><AlertTriangle className="text-amber-500 w-5 h-5"/> Doplnkový technický servis</h4>
                            <ul className="space-y-4 text-sm text-zinc-700 dark:text-zinc-300 font-semibold">
                               {(pricing.additionalServices || []).map(service => (
                                 <li key={service.id} className="flex justify-between items-center">
                                    <span className="max-w-[70%]">{service.name}</span>
                                    <span className="font-black text-zinc-900 dark:text-zinc-50">{service.price}€</span>
                                 </li>
                               ))}
                            </ul>
                            {(pricing.additionalServices || []).some(s => s.name.toLowerCase().includes('chlad')) && (
                                <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-5 leading-relaxed font-medium">
                                   Doplnenie chladiva štandardne nie je potrebné. Riešime ho explicitne iba v prípade, že klimatizácia po vyčistení stále slabo chladí v dôsledku mikroúniku, a je potrebný servis chladiaceho okruhu.
                                </p>
                            )}
                          </div>
                       </div>
                    </div>
                  </motion.div>
                )}
             </AnimatePresence>
           </div>
        </div>
      </section>

      {/* REALIZÁCIE */}
      {showGallery && (
        <section id="realizacie" className="py-16 md:py-24 px-4 sm:px-6 max-w-7xl mx-auto border-t border-zinc-200 dark:border-zinc-800/50">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4 text-zinc-900 dark:text-zinc-50 transition-colors">Naše realizácie.</h2>
            <p className="text-base md:text-lg text-zinc-600 dark:text-zinc-400 font-semibold px-2">Čistá práca, o ktorej nebudete ani vedieť.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
            {galleryImages.length > 0 ? (
              galleryImages.map(img => (
                <div key={img.id} className="aspect-square bg-zinc-200 dark:bg-zinc-800 rounded-2xl md:rounded-[2rem] overflow-hidden hover:scale-[1.02] transition-transform duration-300">
                  <img src={img.url} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
              ))
            ) : (
              <>
                <div className="aspect-square bg-zinc-200 dark:bg-zinc-800 rounded-2xl md:rounded-[2rem] overflow-hidden hover:scale-[1.02] transition-transform duration-300">
                  <img src="https://images.unsplash.com/photo-1631541909061-71e34ddfc2ea?auto=format&fit=crop&q=80&w=600" alt="Čistenie klimatizácie Žilina" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-square bg-zinc-200 dark:bg-zinc-800 rounded-2xl md:rounded-[2rem] overflow-hidden hover:scale-[1.02] transition-transform duration-300">
                  <img src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&q=80&w=600" alt="Detail vyčistenej klímy" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-square bg-zinc-200 dark:bg-zinc-800 rounded-2xl md:rounded-[2rem] overflow-hidden hover:scale-[1.02] transition-transform duration-300">
                  <img src="https://images.unsplash.com/photo-1571781418606-8fb3dd08eb76?auto=format&fit=crop&q=80&w=600" alt="Ochranný vak na klimatizáciu" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
              </>
            )}
            <div className="aspect-square bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl md:rounded-[2rem] overflow-hidden flex items-center justify-center p-4 md:p-6 text-center hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors group cursor-pointer" onClick={() => window.open('https://instagram.com/klimacisto', '_blank')}>
               <div>
                 <Instagram className="w-8 h-8 md:w-12 md:h-12 mx-auto text-pink-500 mb-2 md:mb-3 group-hover:scale-110 transition-transform" />
                 <span className="font-bold text-zinc-700 dark:text-zinc-300 text-sm md:text-lg">Ďalšie fotky<br/>na Instagrame</span>
               </div>
            </div>
          </div>
        </section>
      )}

      {/* SEO LOKALITY SECTION */}
      <section id="lokality" className="py-16 md:py-24 px-4 sm:px-6 bg-zinc-100 dark:bg-[#121214] border-y border-zinc-200 dark:border-zinc-800/50 transition-colors">
        <div className="max-w-5xl mx-auto text-center">
           <div className="w-12 h-12 md:w-16 md:h-16 mx-auto bg-cyan-500/10 text-cyan-500 rounded-xl md:rounded-2xl flex items-center justify-center mb-6 md:mb-8"><MapPin className="w-6 h-6 md:w-8 md:h-8" /></div>
           <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-6 md:mb-8 text-zinc-900 dark:text-zinc-50">Cestujeme za vami naprieč regiónom</h2>
           <p className="text-base md:text-xl text-zinc-600 dark:text-zinc-400 font-semibold leading-relaxed mb-6">
             Naša základňa sídli v srdci mesta <strong className="text-zinc-900 dark:text-zinc-100 font-black">Žilina</strong>, preto je doprava po meste a v úzkom okolí bezplatná. S profesionálnym hĺbkovým čistením však obsluhujeme celý <strong className="text-zinc-900 dark:text-zinc-100 font-black">Žilinský kraj</strong>. Najčastejšie jazdíme do lokalít ako <strong className="text-zinc-900 dark:text-zinc-100 font-black">{seoLocalities}</strong> (aj širšie okolie).
           </p>
           <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 font-bold border-t border-zinc-200 dark:border-zinc-800 pt-6 mt-6 max-w-3xl mx-auto">Máte väčší projekt, firmu, alebo len jednoducho chcete tú najlepšiu starostlivosť o vašu klímu a ste z iného kraja? Žiadny problém, dokážeme po dohode realizovať <strong className="text-zinc-700 dark:text-zinc-300">výjazdy po celom Slovensku</strong>.</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24 px-4 sm:px-6 max-w-4xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-10 md:mb-16 text-zinc-900 dark:text-zinc-50 text-center transition-colors">Časté otázky.</h2>
        <div className="space-y-3 md:space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-zinc-50 dark:bg-[#121214] border border-zinc-200 dark:border-zinc-800/50 rounded-xl md:rounded-2xl overflow-hidden transition-colors">
              <button 
                onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-5 md:p-6 lg:p-8 text-left focus:outline-none focus-visible:ring-2 ring-cyan-500 group"
              >
                <span className="font-extrabold text-base md:text-lg lg:text-xl text-zinc-900 dark:text-zinc-100 pr-4 md:pr-8 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">{faq.q}</span>
                <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-zinc-400 shrink-0 transition-transform ${activeFaq === i ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {activeFaq === i && (
                   <motion.div 
                     initial={{ height: 0, opacity: 0 }}
                     animate={{ height: 'auto', opacity: 1 }}
                     exit={{ height: 0, opacity: 0 }}
                     className="overflow-hidden"
                   >
                     <div className="px-5 md:px-6 lg:px-8 pb-5 md:pb-8 pt-0 border-t border-zinc-100 dark:border-zinc-800/50 mt-2 pt-4 md:pt-6">
                       <p className="text-zinc-600 dark:text-zinc-400 font-semibold text-sm md:text-base lg:text-lg leading-relaxed">{faq.a}</p>
                     </div>
                   </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      {/* KONTAKT & REZERVÁCIA */}
      <section id="kontakt" className="py-16 md:py-24 px-4 sm:px-6 relative">
        <div className="max-w-7xl mx-auto bg-zinc-950 dark:bg-[#121214] border border-zinc-800 dark:border-zinc-800/50 text-zinc-50 rounded-[2rem] md:rounded-[3rem] p-6 sm:p-8 md:p-16 overflow-hidden shadow-2xl transition-colors duration-700">
          <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-cyan-600/15 blur-[150px] rounded-full pointer-events-none"></div>
          
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 relative z-10">
            <div className="lg:col-span-6 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full border border-zinc-800 bg-white/5 backdrop-blur-sm text-xs font-black uppercase tracking-widest mb-8 md:mb-10 text-zinc-300 transform scale-90 sm:scale-100 origin-left">
                   <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> Rezervácie otvorené
                </div>
                <h2 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter mb-4 md:mb-6 leading-[1.05] whitespace-pre-line">{texts.contactTitle}</h2>
                <p className="text-zinc-400 text-lg md:text-xl font-semibold mb-10 md:mb-12 max-w-md tracking-tight whitespace-pre-line">{texts.contactSubtitle}</p>
              </div>

              <div className="space-y-4 md:space-y-6">
                <a href={`tel:${phoneWithoutSpaces}`} className="flex items-center gap-4 md:gap-6 group bg-zinc-900/50 border border-zinc-800/80 p-5 md:p-6 rounded-3xl md:rounded-[2rem] hover:bg-zinc-800 transition-colors">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-cyan-500/10 flex items-center justify-center text-cyan-400 shrink-0">
                    <Phone className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <div className="text-zinc-400 font-bold uppercase tracking-widest text-[10px] mb-1">Volať (Po-Ne, 08:00 - 16:00)</div>
                    <div className="text-xl md:text-2xl font-black tracking-tight">{texts.contactPhone || "+421 911 531 673"}</div>
                  </div>
                </a>

                <a href="https://instagram.com/klimacisto" target="_blank" rel="noreferrer" className="flex items-center gap-4 md:gap-6 group hover:border-pink-500/30 bg-zinc-900/50 border border-zinc-800/80 p-5 md:p-6 rounded-3xl md:rounded-[2rem] hover:bg-zinc-800 transition-colors">
                   <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-pink-500/10 flex items-center justify-center text-pink-400 shrink-0">
                    <Instagram className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <div className="text-zinc-400 font-bold uppercase tracking-widest text-[10px] mb-1">Čerstvé inštalácie na IG</div>
                    <div className="text-xl md:text-2xl font-black tracking-tight group-hover:text-pink-400 transition-colors">@klimacisto</div>
                  </div>
                </a>
              </div>
            </div>

            {/* FORM */}
            <div className="lg:col-span-6 bg-white dark:bg-zinc-900 p-6 sm:p-8 md:p-12 rounded-3xl md:rounded-[2.5rem] relative">
              <h3 className="text-3xl font-black text-zinc-900 dark:text-zinc-50 mb-8 border-b border-zinc-100 dark:border-zinc-800 pb-6 whitespace-pre-line">{texts.contactFormTitle}</h3>
              <form onSubmit={handleFormSubmit} className="flex flex-col gap-6">
                {/* Honeypot */}
                <input 
                  type="text" 
                  name="user_email_address_honeypot" 
                  tabIndex={-1} 
                  autoComplete="off" 
                  className="hidden" 
                  value={formData.honeypot}
                  onChange={e => setFormData({...formData, honeypot: e.target.value})}
                />
                
                <div>
                  <input type="text" aria-label="Vaše Meno" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Vaše Meno" className="w-full bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all font-bold text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400" />
                </div>
                <div>
                  <input type="tel" aria-label="Váš telefón" required value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+421 9_ _  _ _ _  _ _ _" className="w-full bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all font-bold text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400" />
                </div>
                <div>
                  <input type="text" aria-label="Počet jednotiek a typ" required value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} placeholder="Počet a typ (napr. 2x nástenná, 1x vonkajšia)" className="w-full bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-zinc-900 dark:text-zinc-100 font-bold placeholder:text-zinc-400" />
                </div>
                <div>
                   <input type="text" aria-label="Lokalita realizácie" required value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="Lokalita (Mesto/Obec)" className="w-full bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all font-bold text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400" />
                </div>
                
                {/* Ochrana proti spamu - jednoduchá matematika */}
                <div className="flex items-center gap-2 sm:gap-4 bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-4 sm:px-6 py-3 sm:py-4">
                  <div className="flex-1 font-bold text-zinc-600 dark:text-zinc-400 text-xs sm:text-sm">
                    Koľko je <span className="text-zinc-900 dark:text-zinc-100 text-base sm:text-lg">{num1} + {num2}</span>?
                  </div>
                  <input type="number" aria-label="Ochrana proti spamu: zadajte súčet" required value={formData.captcha} onChange={e => setFormData({...formData, captcha: e.target.value})} placeholder="Súčet" className="w-20 sm:w-24 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl px-2 sm:px-4 py-2 text-base sm:text-lg text-center outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all font-black text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400" />
                </div>

                <button disabled={formStatus === 'loading'} className="mt-2 w-full bg-cyan-500 text-zinc-950 font-black text-lg md:text-xl py-4 sm:py-5 rounded-2xl hover:bg-cyan-400 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 uppercase tracking-wider shadow-lg shadow-cyan-500/30 disabled:opacity-50 disabled:scale-100">
                  {formStatus === 'loading' ? 'Odosielam...' : 'Odoslať'}
                  {formStatus !== 'loading' && <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />}
                </button>
                
                {formStatus === 'success' && (
                  <div className="text-emerald-600 dark:text-emerald-400 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl font-bold flex items-center justify-between text-sm mt-2">
                    Správa bola úspešne odoslaná! <ShieldCheck className="w-5 h-5"/>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-red-600 dark:text-red-400 p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl font-bold flex items-center justify-between text-sm mt-2">
                    {formErrorMessage} <AlertTriangle className="w-5 h-5"/>
                  </div>
                )}
                
                <div className="text-center text-zinc-600 dark:text-zinc-400 text-sm font-bold mt-2 flex items-center justify-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-500"/>
                  Vaše údaje sú zabezpečené a šifrované
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <footer className="py-12 px-6 border-t border-zinc-200 dark:border-zinc-800/50 bg-zinc-50 dark:bg-[#09090b] transition-colors mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400 flex items-center justify-center transition-colors">
              <Wind className="w-5 h-5" />
            </div>
            <span className="font-black tracking-tight text-xl dark:text-zinc-50 text-zinc-900 transition-colors">KlímaČisto</span>
          </div>
          <div className="text-zinc-600 dark:text-zinc-400 text-sm font-bold tracking-wide flex items-center gap-2">
             <MapPin className="w-4 h-4" /> {texts.footerText} • © {new Date().getFullYear()}
          </div>
           {/* Footer info s odkazmi */}
           <div className="text-zinc-600 dark:text-zinc-400 text-xs text-center md:text-right font-medium flex flex-wrap gap-4 justify-center md:justify-end mt-4 lg:mt-0">
             <Link to="/ochrana-osobnych-udajov" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">Ochrana osobných údajov</Link>
             <button onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event('openCookieConsent')); }} className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">Cookies</button>
           </div>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}
